import { ApiServiceCredentials } from '@barbanet/api-service'
import { defineStore } from 'pinia'

import { computed, inject, ref } from 'vue'
import { VueCookies } from 'vue-cookies'

import { useExceptionHandler } from '@barbanet/ui-kit/composables'

import {
	getCurrentRoleService,
	getRoleListService,
	userMeService
} from '../domains/user/services/user'
import {
	OperatorRoleResponse,
	UserMeResponse
} from '../domains/user/types/dto/user'
import { CREDENTIALS_COOKIE } from '../helpers/cookie'

interface AccountInfo {
	user: Nullable<UserMeResponse>
	currentRole: Nullable<OperatorRoleResponse>
	roles: OperatorRoleResponse[]
	loading: boolean
}

export const useAccountStore = defineStore('account', () => {
	const $cookies = inject<VueCookies>('$cookies')
	const router = useRouter()
	const route = useRoute()

	// get account from cookies if exists to set as initial value
	let accountFromCookie = $cookies?.get(CREDENTIALS_COOKIE.key)
	if (accountFromCookie) {
		accountFromCookie = accountFromCookie as BarbanetCredentials
	}

	const credentials = ref<Nullable<BarbanetCredentials>>(
		accountFromCookie || null
	)
	const isLoggedIn = computed(() => !!credentials.value)

	const accountInfo = ref<AccountInfo>({
		user: null,
		currentRole: null,
		roles: [],
		loading: false
	})

	const logout = () => {
		credentials.value = null
		$cookies?.remove(CREDENTIALS_COOKIE.key)
		accountInfo.value = {
			user: null,
			currentRole: null,
			roles: [],
			loading: false
		}

		router.replace({ name: 'AuthenticatePage', query: { to: route.fullPath } })
	}

	const setCredentials = (
		data: BarbanetCredentials | ApiServiceCredentials
	) => {
		const newCredentials = { ...(credentials.value || {}), ...data }

		if (newCredentials.userId) {
			credentials.value = newCredentials as BarbanetCredentials
			$cookies?.set(
				CREDENTIALS_COOKIE.key,
				newCredentials,
				CREDENTIALS_COOKIE.expires,
				CREDENTIALS_COOKIE.path,
				undefined,
				CREDENTIALS_COOKIE.secure,
				CREDENTIALS_COOKIE.sameSite
			)
		} else {
			logout()
		}
	}

	const syncAccountInfo = async () => {
		if (isLoggedIn.value) {
			accountInfo.value.loading = true

			try {
				const meResponse = await userMeService()
				if (meResponse && meResponse.data) {
					accountInfo.value.user = meResponse.data.content
				}

				const roleResponse = await getCurrentRoleService()
				if (roleResponse && roleResponse.data) {
					accountInfo.value.currentRole = roleResponse.data.content
				}

				const rolesResponse = await getRoleListService()
				if (rolesResponse && rolesResponse.data) {
					accountInfo.value.roles = rolesResponse.data.content
				}
			} catch (error) {
				useExceptionHandler(error)
			} finally {
				accountInfo.value.loading = false
			}
		} else {
			console.error('User is not logged in')
		}
	}

	return {
		credentials,
		isLoggedIn,
		setCredentials,
		logout,
		syncAccountInfo,
		accountInfo
	}
})
